import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import {
  Container,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Backdrop,
  Box,
  Button,
  FormControlLabel,
  Modal,
  Switch,
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import {
  BCPLinkSimple,
  BCPSquarePlus,
  BCPUserMagnifyingGlass,
  BCPXmark,
} from "../../../components/icons/bcp/BCPIcons";

import { handlePagingAll } from "../../../components/api/APIUtilities";
import ListResponsive from "../../../components/other/ListResponsive";
import { AdminTabs } from "../Admin";
import {
  getData,
  postData,
} from "../../../components/utilities/UtilityFunctions";

export default function AdminEventUsers(props) {
  const { activeTab, handleTabChange, authContext } = props;
  const { t } = useTranslation("translation", {
    keyPrefix: "pages.admin"
  });

  const [processing, setProcessing] = useState(false);
  const [subLoading, setSubLoading] = useState(true);

  const [users, setUsers] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const check = async () => {
      var apiUsers = await handlePagingAll(`/users`, {
        limit: 100,
        isEventUser: true,
      });
      setUsers(apiUsers);
      setSubLoading(false);
    };
    check();
  }, []);

  const roleLabel = (row) => {
    var roles = [];
    if (row.isOrganizer) {
      roles.push("Organizer");
    }
    if (row.isJudge) {
      roles.push("Judge");
    }
    return roles.join(", ");
  };

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondary",
          }}
        >
          <Typography variant="h3">{t("adminSettings")}</Typography>

          <AdminTabs
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondary",
          }}
        >
          <ListResponsive
            originalList={subLoading ? null : users}
            doSearch
            doPages
            initialRowsPerPage={24}
            actionButton={[
              {
                props: {
                  variant: "contained",
                  color: "primary",
                  onClick: () => {
                    setUser({});
                  },
                  startIcon: <BCPSquarePlus />,
                },
                label: "Add Event User",
              },
            ]}
            searchProps={{
              label: "Search Event User",
              performSearch: (originalList, searchText) => {
                if (!searchText || searchText === "") {
                  return originalList;
                } else {
                  var filteredList = originalList.filter(function (el) {
                    if (el?.firstName?.toLowerCase().includes(searchText))
                      return true;
                    if (el?.lastName?.toLowerCase().includes(searchText))
                      return true;
                    if (el?.email?.toLowerCase().includes(searchText))
                      return true;

                    return false;
                  });
                  return filteredList;
                }
              },
            }}
            rowProps={{
              mapFunction: (row, index) => (
                <Grid
                  key={index}
                  item
                  xs={12}
                  onClick={() => {
                    setUser(row);
                  }}
                  sx={{
                    p: 1,
                    mb: 1,
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1,
                    "&:hover": {
                      cursor: "pointer",
                    },
                  }}
                >
                  <Typography variant="body1">{`${row.firstName} ${row.lastName}`}</Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      margin: 0,
                      fontWeight: "400",
                      fontSize: "0.75rem",
                      lineHeight: "1.66",
                      letterSpacing: "0.03333em",
                    }}
                  >
                    {row.email}
                  </Typography>
                  <Typography variant="caption">{roleLabel(row)}</Typography>
                </Grid>
              ),
            }}
          />
        </Grid>
      </Grid>

      <UserModal
        user={user}
        setUser={setUser}
        users={users}
        setUsers={setUsers}
        setProcessing={setProcessing}
      />

      {Boolean(processing) && <ModalLoadingFull />}
    </Container>
  );
}

function UserModal(props) {
  const { user, setUser, users, setUsers, setProcessing } = props;

  const searchUser = async (email, setFieldValue) => {
    var apiUser;
    if (email !== "") {
      setProcessing(true);
      try {
        apiUser = await getData(`/users/${email}`, {
          queryStringParameters: {},
        });
      } catch (err) {}
      setProcessing(false);
    }

    if (
      apiUser?.id &&
      users.filter(function (el) {
        return el?.id === apiUser.id;
      }).length > 0
    ) {
      postNotification("error", "User is already an Organizer");

      setFieldValue("userId", null);
      setFieldValue("firstName", "");
      setFieldValue("lastName", "");
      return;
    }
    setFieldValue("userId", apiUser?.id || null);
    setFieldValue("firstName", apiUser?.firstName || "");
    setFieldValue("lastName", apiUser?.lastName || "");

    if (!apiUser?.id) {
      postNotification("error", "User not found");
    }
  };

  return (
    <Modal
      disableAutoFocus
      open={Boolean(user)}
      onClose={() => {
        setUser(null);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{ zIndex: 9999 }}
    >
      {user ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setUser(null);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs>
              <Typography variant="h4" component="h2">
                {user.id ? "Update Event User" : "Set Event User"}
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              userId: user?.id || null,
              email: user?.email || "",
              firstName: user?.firstName || "",
              lastName: user?.lastName || "",
              isOrganizer:
                user?.isOrganizer !== undefined ? user.isOrganizer : false,
              isJudge: user?.isJudge !== undefined ? user.isJudge : false,
            }}
            enableReinitialize
            onSubmit={async (values) => {
              var apiData;
              setProcessing(true);
              try {
                var sanitizedValues = {
                  isOrganizer: values.isOrganizer,
                  isJudge: values.isJudge,
                };

                if (values.userId) {
                  var body = {
                    set: sanitizedValues,
                  };
                  apiData = await postData(`/users/${values.userId}`, {
                    body: body,
                  });
                } else {
                  throw new Error("Please select a valid user");
                }

                var newUsers;
                if (user.id) {
                  newUsers = [];
                  users.forEach((currentUser) => {
                    if (currentUser.id !== user.id) {
                      newUsers.push(currentUser);
                    } else {
                      if (apiData.isOrganizer || apiData.isJudge) {
                        newUsers.push(apiData);
                      }
                    }
                  });
                } else if (apiData.isOrganizer || apiData.isJudge) {
                  newUsers = [apiData, ...users];
                } else {
                  newUsers = users;
                }
                setUsers(newUsers);
                setUser(null);
                setProcessing(false);
                return;
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      name="email"
                      value={values.email}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled={Boolean(user.id)}
                      required
                      InputProps={{
                        endAdornment: user.id ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Copy Email"
                              edge="end"
                              color="primary"
                              onClick={() => {
                                navigator.clipboard.writeText(values.email);
                              }}
                            >
                              <BCPLinkSimple />
                            </IconButton>
                          </InputAdornment>
                        ) : (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Search"
                              edge="end"
                              color="primary"
                              onClick={async () => {
                                await searchUser(values.email, setFieldValue);
                              }}
                            >
                              <BCPUserMagnifyingGlass />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onBlur={async (e) => {
                        handleBlur(e);
                        await searchUser(e.target.value, setFieldValue);
                      }}
                      onChange={handleChange}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="First Name"
                      value={values.firstName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Last Name"
                      value={values.lastName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Organizer"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.isOrganizer}
                          onChange={(e) => {
                            setFieldValue("isOrganizer", !values.isOrganizer);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Judge"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.isJudge}
                          onChange={() => {
                            setFieldValue("isJudge", !values.isJudge);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setUser(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                      disabled={!values.userId}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      ) : (
        <></>
      )}
    </Modal>
  );
}
